import React from 'react';

import { Section } from '@/models';

import styles from './index.module.scss';

interface RichTextProps {
  section: Section;
  isKioskMode?: boolean;
}

/**
 * Process HTML content to remove external links and iframes when in kiosk mode
 * @param htmlContent The original HTML content
 * @param isKioskMode Whether kiosk mode is enabled
 * @returns Processed HTML with external links and iframes removed if in kiosk mode
 */
const processHtmlContent = (
  htmlContent: string,
  isKioskMode: boolean,
): string => {
  // If not in kiosk mode, return the original content
  if (!isKioskMode) {
    return htmlContent;
  }

  // Process the HTML content
  let processedHtml = htmlContent;

  // 1. Regex to identify external links (starting with http://, https://, or //)
  const externalLinkRegex =
    /<a\s+(?:[^>]*?\s+)?href=["']((?:http|https):\/\/|\/\/|tel:|mailto:|sms:)[^"']*["']([^>]*)>(.*?)<\/a>/gi;

  // Replace external links with just their content
  processedHtml = processedHtml.replace(
    externalLinkRegex,
    (match, protocol, attrs, content) => {
      // Return just the content without the link
      return content;
    },
  );

  // 2. Regex to identify iframes
  const iframeRegex = /<iframe\b[^>]*>[\s\S]*?<\/iframe>|<iframe\b[^>]*\/>/gi;

  // Remove iframes completely
  processedHtml = processedHtml.replace(iframeRegex, () => {
    return '';
  });

  return processedHtml;
};

const RichText = ({ section, isKioskMode = false }: RichTextProps) => {
  // Process the HTML content if we have any
  const processedHtml = section.html_text
    ? processHtmlContent(section.html_text, isKioskMode)
    : '';

  return (
    <div
      className={styles.container}
      dangerouslySetInnerHTML={
        processedHtml
          ? {
              __html: processedHtml,
            }
          : undefined
      }
    />
  );
};

export default RichText;
